import React, { useState, useContext } from 'react'
import { FooterProps } from '../index'
import { Grid, Button, Logo } from '@deal/components'
import { Link } from 'react-router-dom'
import config from '#src/app/config'
import { AnalyticsContext } from '#src/app/containers/Analytics'
import classnames from 'classnames'
import backgroundImage from './background.jpg'
import LetterIcon from './letter.svg'
import styles from './styles.css'

enum MarketingFooterSection {
  Categories,
  About,
  Social,
  Legal
}

const MarketingFooter: React.FC<FooterProps> = () => {
  const analytics = useContext(AnalyticsContext)
  const [expandedSection, setExpandedSection] = useState<MarketingFooterSection | null>(null)

  const areas = `
    "categories categories"
    "about      about"
    "social     social"
    "legal      legal"
    "login      login"
  `

  const areasSm = `
    "categories categories categories categories about about about about"
    "social     social     social     social     legal legal legal legal"
    "login      login      login      login      login login login login"
  `

  const areasMd = `
    "categories categories categories about about about social social social login login login"
    "categories categories categories about about about legal  legal  legal  login login login"
  `

  return (
    <footer
      className={styles.footer}
      style={{
        backgroundImage: `url(${backgroundImage}?w=1440)`
      }}
    >
      <Grid areas={areas} areasSm={areasSm} areasMd={areasMd}>
        {({ Categories, About, Social, Legal, Login }) => {
          return (
            <>
              <Categories
                className={classnames(styles.section, {
                  [styles.sectionExpanded]: expandedSection === MarketingFooterSection.Categories
                })}
              >
                <div
                  className={styles.sectionTitle}
                  onClick={() =>
                    setExpandedSection(
                      expandedSection === MarketingFooterSection.Categories
                        ? null
                        : MarketingFooterSection.Categories
                    )
                  }
                >
                  Shop <span className={styles.sectionTitleArrow} />
                </div>
                <ul className={styles.sectionList}>
                  <li className={styles.sectionListItem}>
                    <Link to={'/d/winter-sports'} className={styles.sectionListItemLink}>
                      Winter Sports
                    </Link>
                  </li>
                  <li className={styles.sectionListItem}>
                    <Link to={'/d/golf'} className={styles.sectionListItemLink}>
                      Golf
                    </Link>
                  </li>
                  <li className={styles.sectionListItem}>
                    <Link to={'/d/cycling'} className={styles.sectionListItemLink}>
                      Cycling
                    </Link>
                  </li>
                  <li className={styles.sectionListItem}>
                    <Link to={'/d/camping'} className={styles.sectionListItemLink}>
                      Camping & Hiking
                    </Link>
                  </li>
                  <li className={styles.sectionListItem}>
                    <Link to={'/d/fishing'} className={styles.sectionListItemLink}>
                      Fly Fishing
                    </Link>
                  </li>
                </ul>
              </Categories>
              <About
                className={classnames(styles.section, {
                  [styles.sectionExpanded]: expandedSection === MarketingFooterSection.About
                })}
              >
                <div
                  className={styles.sectionTitle}
                  onClick={() =>
                    setExpandedSection(
                      expandedSection === MarketingFooterSection.About
                        ? null
                        : MarketingFooterSection.About
                    )
                  }
                >
                  About <span className={styles.sectionTitleArrow} />
                </div>
                <ul className={styles.sectionList}>
                  <li className={styles.sectionListItem}>
                    <Link to="/how-it-works" className={styles.sectionListItemLink}>
                      How it works
                    </Link>
                  </li>
                  <li className={styles.sectionListItem}>
                    <Link to="/meet-the-experts" className={styles.sectionListItemLink}>
                      Meet the experts
                    </Link>
                  </li>
                  <li className={styles.sectionListItem}>
                    <Link to="/about-us" className={styles.sectionListItemLink}>
                      About Curated
                    </Link>
                  </li>
                  <li className={styles.sectionListItem}>
                    <Link
                      to="/journal"
                      className={styles.sectionListItemLink}
                      onClick={() =>
                        analytics?.events.trackArticleHubEntryPointClicked({
                          entry_point: 'footer'
                        })
                      }
                    >
                      Expert Journal
                    </Link>
                  </li>
                  <li className={styles.sectionListItem}>
                    <Link to="/reviews" className={styles.sectionListItemLink}>
                      Reviews
                    </Link>
                  </li>
                  <li className={styles.sectionListItem}>
                    <Link to="/faq" className={styles.sectionListItemLink}>
                      FAQ
                    </Link>
                  </li>
                  <li className={styles.sectionListItem}>
                    <Link to="/team" className={styles.sectionListItemLink}>
                      Meet the team
                    </Link>
                  </li>
                  <li className={styles.sectionListItem}>
                    <Link to="/careers" className={styles.sectionListItemLink}>
                      Careers
                    </Link>
                  </li>
                </ul>
              </About>
              <Social
                className={classnames(styles.section, {
                  [styles.sectionExpanded]: expandedSection === MarketingFooterSection.Social
                })}
              >
                <div
                  className={styles.sectionTitle}
                  onClick={() =>
                    setExpandedSection(
                      expandedSection === MarketingFooterSection.Social
                        ? null
                        : MarketingFooterSection.Social
                    )
                  }
                >
                  Social <span className={styles.sectionTitleArrow} />
                </div>
                <ul className={styles.sectionList}>
                  <li className={styles.sectionListItem}>
                    <a
                      href={config.get('facebookLink')}
                      target="_blank"
                      className={styles.sectionListItemLink}
                    >
                      Facebook
                    </a>
                  </li>
                  <li className={styles.sectionListItem}>
                    <a
                      href={config.get('instagramLink')}
                      target="_blank"
                      className={styles.sectionListItemLink}
                    >
                      Instagram
                    </a>
                  </li>
                  <li className={styles.sectionListItem}>
                    <a
                      href={config.get('youtubeLink')}
                      target="_blank"
                      className={styles.sectionListItemLink}
                    >
                      YouTube
                    </a>
                  </li>
                </ul>
              </Social>
              <Legal
                className={classnames(styles.section, {
                  [styles.sectionExpanded]: expandedSection === MarketingFooterSection.Legal
                })}
              >
                <div
                  className={styles.sectionTitle}
                  onClick={() =>
                    setExpandedSection(
                      expandedSection === MarketingFooterSection.Legal
                        ? null
                        : MarketingFooterSection.Legal
                    )
                  }
                >
                  Legal <span className={styles.sectionTitleArrow} />
                </div>
                <ul className={styles.sectionList}>
                  <li className={styles.sectionListItem}>
                    <Link to="/legal/terms-conditions" className={styles.sectionListItemLink}>
                      Terms of use
                    </Link>
                  </li>
                  <li className={styles.sectionListItem}>
                    <Link to="/legal/privacy-policy" className={styles.sectionListItemLink}>
                      Privacy policy
                    </Link>
                  </li>
                </ul>
              </Legal>
              <Login className={styles.sectionLogin}>
                <div className={styles.sectionSubSection}>
                  <div className={styles.sectionTitle}>Already a member?</div>
                  <Link to="/auth/login" rel="nofollow">
                    <Button variant="important" size="small" className={styles.button}>
                      Login
                    </Button>
                  </Link>
                </div>
                <div className={styles.sectionSubSection}>
                  <div className={styles.sectionTitle}>Want to be an expert?</div>
                  <Link to="/apply">
                    <Button variant="important" size="small" className={styles.button}>
                      Apply
                    </Button>
                  </Link>
                </div>
                <div className={styles.sectionSubSection}>
                  <div className={styles.sectionTitle}>Need help?</div>
                  <a href="mailto:support@curated.com" className={styles.contactEmail}>
                    <LetterIcon className={styles.contactIcon} /> support@curated.com
                  </a>
                </div>
              </Login>
            </>
          )
        }}
      </Grid>
      <div className={styles.logo}>
        <Logo mark="icon" color="light" className={styles.logoMark} />
      </div>
    </footer>
  )
}

export default MarketingFooter
